// src/components/dashboard/CourseListPanel.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./courselist_panel.css";

const COURSE_EXTENSION_PAYMENT_LINK = "https://square.link/u/VBauMzNW";

/**
 * Reusable course list panel (Student "My Courses" + GM "Courses Overview")
 *
 * Expects courses shaped like:
 * {
 *   key,
 *   title,
 *   track,
 *   startDate,
 *   expiryDate,
 *   status,
 *   prepCompleted,
 *   hasStarted,
 *   mainUnlocked,
 *   progressPercent,
 *   currentSectionTitle,
 *   enrollmentId,
 *   postCourseCompleted,
 *   postCourseProgress,          // ✅ add this from Dashboard mapping
 *   postCourseNeedsAttention
 * }
 */
const CourseListPanel = ({
  title,
  subtitle,
  courses = [],
  emptyText,
  formatDateUTC,
  postCourseAttentionByEnrollmentId,
  postCourseModulesByKey,
  allowExtensionPurchaseLink = false,
}) => {
  const [openByKey, setOpenByKey] = useState({});

  useEffect(() => {
    setOpenByKey({});
  }, [courses]);

  const getPostCourseStatus = (course) => {
    if (!course) return null;

    if (course.postCourseCompleted) {
      return { key: "approved", label: "Approved ✅", className: "dash-pill dash-pill-approved" };
    }

    const prog = Array.isArray(course.postCourseProgress) ? course.postCourseProgress : [];

    const hasChanges = prog.some((p) => String(p?.status || "").toLowerCase() === "changes_requested");
    if (hasChanges) {
      return { key: "changes", label: "Changes Requested", className: "dash-pill dash-pill-changes" };
    }

    const hasSubmitted = prog.some((p) => String(p?.status || "").toLowerCase() === "submitted");
    if (hasSubmitted) {
      return { key: "pending", label: "Pending Review", className: "dash-pill dash-pill-pending" };
    }

    return null;
  };

  const isUnlockedOnStartDate = (startDate) => {
  if (!startDate) return false;
  const d = new Date(startDate);
  if (Number.isNaN(d.getTime())) return false;

  // unlock on Day 1 at local midnight
  const startLocalMidnight = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Date.now() >= startLocalMidnight;
};


  const renderCourseList = () => {
    if (!courses || courses.length === 0) {
      return <p className="dash-empty-text">{emptyText}</p>;
    }

    return (
      <div className="dash-course-grid">
        {[...courses]
          .sort((a, b) => {
            const aTime = a?.startDate ? new Date(a.startDate).getTime() : Infinity;
            const bTime = b?.startDate ? new Date(b.startDate).getTime() : Infinity;
            if (aTime !== bTime) return aTime - bTime;

            const aTitle = (a?.title || "").toLowerCase();
            const bTitle = (b?.title || "").toLowerCase();
            return aTitle.localeCompare(bTitle);
          })
          .map((course) => {
            const key = course.key || course.courseKey || course._id;
            const title = course.title || "Untitled Course";

            const isExpired =
              course.status === "expired" ||
              course.expired ||
              (!!course.expiryDate && new Date(course.expiryDate).getTime() < Date.now());

            const startDate = course.startDate;
            const expiryDate = course.expiryDate;

const prepCompleted = !!course.prepCompleted;
const postCourseUnlockedByDate = isUnlockedOnStartDate(startDate);

// ✅ Require both prep completion and class start date
const postCourseUnlocked = prepCompleted && postCourseUnlockedByDate;

            const enrollmentId = String(course.enrollmentId || course.enrollment?._id || "");

            const needsPostCourseAttention =
              !!course.postCourseNeedsAttention || (!!enrollmentId && !!postCourseAttentionByEnrollmentId?.[enrollmentId]);

            const progressPercent =
              typeof course.progressPercent === "number"
                ? Math.max(0, Math.min(100, course.progressPercent))
                : prepCompleted
                ? 100
                : 0;
            const postSections = Array.isArray(postCourseModulesByKey?.[key]?.sections)
              ? postCourseModulesByKey[key].sections
              : [];
            const postPassedCount = Array.isArray(course.postCourseProgress)
              ? course.postCourseProgress.filter(
                  (p) => String(p?.status || "").toLowerCase() === "passed"
                ).length
              : 0;
            const postCourseProgressPercent =
              postSections.length > 0
                ? Math.max(0, Math.min(100, Math.round((postPassedCount / postSections.length) * 100)))
                : course.postCourseCompleted
                ? 100
                : 0;

            const currentSectionTitle = course.currentSectionTitle || null;

            const postStatus = getPostCourseStatus(course);

            const showCourseAlertTriangle =
  postStatus?.key === "changes";


            return (
              <div key={key} className={`dash-course-card ${isExpired ? "dash-course-card-expired" : ""}`}>
                <button
                  type="button"
                  className="dash-course-collapse-head"
                  onClick={() => setOpenByKey((prev) => ({ ...prev, [key]: !prev[key] }))}
                  aria-expanded={!!openByKey[key]}
                  aria-label={`${openByKey[key] ? "Collapse" : "Expand"} ${title}`}
                >
                  <div className="dash-course-collapse-main">
                    <div className="dash-course-title-row">
                      <div className="dash-course-title">{title}</div>
                      {showCourseAlertTriangle && (
                        <span
                          className="dash-course-alert-tri dash-course-alert-tri-inline"
                          title={postStatus?.key === "changes" ? "Changes requested" : "Pending review"}
                          aria-label={postStatus?.key === "changes" ? "Changes requested" : "Pending review"}
                        >
                          <span>!</span>
                        </span>
                      )}
                      {postStatus?.key === "changes" && (
                        <span className="dash-pill dash-pill-changes dash-course-collapsed-status">
                          Changes Requested
                        </span>
                      )}
                    </div>
                    <div className="dash-course-progress-group">
                      <div className="dash-course-progress">
                        <div className="dash-course-progress-label">Prep Course</div>
                        <div
                          className="dash-course-progress-bar"
                          role="progressbar"
                          aria-label={`${title} prep progress`}
                          aria-valuenow={progressPercent}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        >
                          <div className="dash-course-progress-fill" style={{ width: `${progressPercent}%` }} />
                        </div>
                        <span className="dash-course-progress-text">{progressPercent}% complete</span>
                      </div>
                      <div className="dash-course-progress">
                        <div className="dash-course-progress-label">Post-Course</div>
                        <div
                          className={[
                            "dash-course-progress-bar",
                            "dash-course-progress-bar-post",
                            !postCourseUnlocked ? "is-locked" : "",
                          ].join(" ")}
                          role="progressbar"
                          aria-label={`${title} post-course progress`}
                          aria-valuenow={postCourseProgressPercent}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        >
                          <div
                            className={[
                              "dash-course-progress-fill",
                              "dash-course-progress-fill-post",
                              !postCourseUnlocked ? "is-locked" : "",
                            ].join(" ")}
                            style={{ width: `${postCourseProgressPercent}%` }}
                          />
                        </div>
                        <span className={`dash-course-progress-text ${!postCourseUnlocked ? "is-locked" : ""}`}>
                          {postCourseUnlocked ? `${postCourseProgressPercent}% complete` : "Locked"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <span className={`dash-course-collapse-caret ${openByKey[key] ? "is-open" : ""}`}>▾</span>
                </button>

                {!!openByKey[key] && (
                  <>
                    <div className="dash-course-badges">
                      {needsPostCourseAttention && (
                        <span className="dash-attn-pill dash-attn-pill-shimmer">Review update</span>
                      )}

                      {course.postCourseCompleted && (
                        <span className="dash-badge dash-badge-platinum">Post-Course Complete</span>
                      )}

                      {isExpired && <span className="dash-badge dash-badge-bad">Expired</span>}
                    </div>

                    {startDate && <div className="dash-course-meta grn">Class start: {formatDateUTC(startDate)}</div>}
                    {expiryDate && <div className="dash-course-meta">Access until: {formatDateUTC(expiryDate)}</div>}

                    {currentSectionTitle && (
                      <div className="dash-course-meta">
                        Section you’re on:{" "}
                        <span className="dash-course-meta-strong">{currentSectionTitle}</span>
                      </div>
                    )}

                    {isExpired ? (
                      <>
                        <div className="dash-course-note" style={{ marginTop: 10 }}>
                          This course access has expired. Your progress is saved, and access can be extended for 3 months.
                        </div>
                        {allowExtensionPurchaseLink && (
                          <a
                            href={COURSE_EXTENSION_PAYMENT_LINK}
                            target="_blank"
                            rel="noreferrer"
                            className="dash-course-cta dash-course-cta-secondary"
                          >
                            Extend Access 3 Months — $150
                          </a>
                        )}
                      </>
                    ) : (
                      <>
                        {key && (
                          <Link to={`/courses/prep/${key}`} className="dash-course-cta">
                            {prepCompleted ? "Review Prep Course" : "Open Prep Course"}
                          </Link>
                        )}

                        {key && (
                          <>
                            {postCourseUnlocked ? (
                              <div className="dash-postcourse-row">
                                <Link
                                  to={`/courses/post/${key}`}
                                  className={[
                                    "dash-course-cta",
                                    "dash-course-cta-secondary",
                                    postStatus?.key === "pending" || postStatus?.key === "changes" ? "dash-course-cta-attn" : "",
                                  ]
                                    .filter(Boolean)
                                    .join(" ")}
                                >
                                  {postStatus?.key === "pending"
                                    ? "Post-Course Module"
                                    : postStatus?.key === "changes"
                                    ? "Review Changes Requested"
                                    : "Post-Course Module"}
                                </Link>

                                {postStatus && <span className={postStatus.className}>{postStatus.label}</span>}
                              </div>
                            ) : (
                              <div className="dash-course-note">
                                {!prepCompleted
                                  ? "Complete your prep course to unlock post-course."
                                  : startDate
                                  ? `Post-course unlocks on your class date: ${formatDateUTC(startDate)}.`
                                  : "Post-course unlocks on your class date."}
                              </div>
                            )}
                          </>
                        )}
                      </>
                    )}

                    {!isExpired && postStatus?.key === "approved" && (
                      <div className="dash-postcourse-nextstep">
                        Next step: <strong>Apprenticeship Mode</strong>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
      </div>
    );
  };

  return (
    <div className="dash-panel">
      <h2 className="dash-panel-title">{title}</h2>
      {subtitle && <p className="dash-panel-subtitle">{subtitle}</p>}
      {renderCourseList()}
    </div>
  );
};

export default CourseListPanel;
